import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getAdminBalanceLedger } from "../api/api";
import "../css/RetailersPage.css";

interface Retailer {
  retailer_id_pk: number;
  retailer_name?: string;
  fullname?: string;
  mobile_no?: string;
}

interface LedgerEntry {
  admin_balance_ladger_id_pk: number;
  admin_id_fk: number;
  retailer_id_fk: number;
  retailer?: Retailer | null;
  amount: number;
  transaction_type: "credit" | "debit";
  previous_balance: number;
  current_balance: number;
  created_at: string;
}

interface Pagination {
  total: number;
  count: number;
  per_page: number;
  current_page: number;
  total_pages: number;
  has_more: boolean;
}

export default function AdminBalanceLedger() {
  const { user } = useAuth() as any;
  const navigate = useNavigate();

  const [fromDate, setFromDate]         = useState("");
  const [toDate, setToDate]             = useState("");
  const [search, setSearch]             = useState("");
  const [txnType, setTxnType]           = useState("");
  const [entries, setEntries]           = useState<LedgerEntry[]>([]);
  const [pagination, setPagination]     = useState<Pagination | null>(null);
  const [page, setPage]                 = useState(1);
  const [perPage, setPerPage]           = useState(15);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");

  // Redirect retailer away
  useEffect(() => {
    const role = (user?.role || user?.user_type || "").toString().toLowerCase();
    if (role === "retailer") {
      navigate("/retailers", { replace: true });
    }
  }, [user, navigate]);

  const fetchLedger = async (overridePage = page) => {
    if (!user?.token) {
      setError("Not authenticated");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await getAdminBalanceLedger(
        {
          login_id: user.id,
          transaction_type: txnType,
          search,
          from: fromDate,
          to: toDate,
          per_page: perPage,
          page: overridePage,
        },
        user.token
      );

      if (res?.success) {
        const payload = res?.data;
        const rows: LedgerEntry[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        const apiPagination = payload?.pagination || {};
        const hasPaginationMeta =
          apiPagination.total !== undefined ||
          apiPagination.total_pages !== undefined ||
          apiPagination.current_page !== undefined ||
          payload?.total !== undefined ||
          payload?.total_pages !== undefined ||
          payload?.current_page !== undefined;

        const total = Number(
          apiPagination.total ??
            payload?.total ??
            (hasPaginationMeta
              ? rows.length
              : (overridePage - 1) * perPage + rows.length)
        );
        const currentPage = Number(
          apiPagination.current_page ?? payload?.current_page ?? overridePage
        );
        const pageSize = Number(
          apiPagination.per_page ?? payload?.per_page ?? perPage
        );
        const inferredHasMore = rows.length === pageSize;
        const totalPages = Number(
          apiPagination.total_pages ??
            payload?.total_pages ??
            (hasPaginationMeta
              ? Math.max(1, Math.ceil(total / (pageSize || perPage)))
              : currentPage + (inferredHasMore ? 1 : 0))
        );
        const hasMore =
          typeof apiPagination.has_more === "boolean"
            ? apiPagination.has_more
            : hasPaginationMeta
              ? currentPage < totalPages
              : inferredHasMore;

        setEntries(rows);
        setPagination({
          total,
          count: Number(apiPagination.count ?? payload?.count ?? rows.length),
          per_page: pageSize,
          current_page: currentPage,
          total_pages: totalPages,
          has_more: hasMore,
        });
      } else {
        setError(res?.message || "Failed to fetch ledger.");
      }
    } catch (err: any) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
  }, [user?.token, page, perPage]);

  const handleSearch = () => {
    setPage(1);
    fetchLedger(1);
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setSearch("");
    setTxnType("");
    setPerPage(15);
    setPage(1);
    setTimeout(() => fetchLedger(1), 0);
  };

  const handlePerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPerPage(Number(e.target.value));
    setPage(1);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="retailer-page">

      {/* Filter Box */}
      <div className="card">
        <div className="card-header">Filter Box</div>
        <div className="card-body">
          <p>
            Search by retailer name, mobile, transaction type, or date range.
          </p>

          <div className="filter-row" style={{ flexWrap: "wrap", gap: "10px" }}>
            <input
              type="text"
              placeholder="Search retailer name / mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 220 }}
            />

            <select
              value={txnType}
              onChange={(e) => setTxnType(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="credit">Credit</option>
              <option value="debit">Debit</option>
            </select>

            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />

            <select
              value={perPage}
              onChange={handlePerPageChange}
              style={{
                width: 140,
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: 4,
                background: "#fff",
              }}
            >
              <option value={5}>Page Size: 5</option>
              <option value={10}>Page Size: 10</option>
              <option value={15}>Page Size: 15</option>
              <option value={100}>Page Size: 100</option>
            </select>

            <button className="btn-search" onClick={handleSearch}>
              Search
            </button>

            <button
              className="btn-search"
              onClick={handleReset}
              style={{ background: "#64748b" }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="card mt-4">
        <div className="card-header card-between">
          <span>Admin Balance Ledger</span>
          {pagination && (
            <span style={{ fontSize: 13, color: "#64748b" }}>
              Total: {pagination.total} records
            </span>
          )}
        </div>

        <div className="table-responsive">
          {loading && <p style={{ padding: "1rem" }}>Loading...</p>}
          {error && <p style={{ padding: "1rem", color: "red" }}>{error}</p>}

          {!loading && !error && (
            <table className="log-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Retailer</th>
                  <th>Mobile</th>
                  <th>Type</th>
                  <th>Debit</th>
                  <th>Credit</th>
                  <th>Previous Balance</th>
                  <th>Current Balance</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center" }}>
                      No records found.
                    </td>
                  </tr>
                ) : (
                  entries.map((item) => (
                    <tr key={item.admin_balance_ladger_id_pk}>
                      <td>
                        <b>#{item.admin_balance_ladger_id_pk}</b>
                      </td>
                      <td>{item.retailer?.fullname || item.retailer?.retailer_name || "—"}</td>
                      <td>{item.retailer?.mobile_no || "—"}</td>
                      <td>
                        <span
                          style={{
                            color:
                              item.transaction_type === "credit"
                                ? "green"
                                : "red",
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          {item.transaction_type}
                        </span>
                      </td>
                      <td>
                        {item.transaction_type === "debit"
                          ? item.amount.toFixed(2)
                          : "—"}
                      </td>
                      <td>
                        {item.transaction_type === "credit"
                          ? item.amount.toFixed(2)
                          : "—"}
                      </td>
                      <td>{item.previous_balance.toFixed(2)}</td>
                      <td>{item.current_balance.toFixed(2)}</td>
                      <td>{formatDate(item.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              borderTop: "1px solid #e2e8f0",
              fontSize: 13,
            }}
          >
            <span>
              Page {pagination.current_page} of {pagination.total_pages} - {" "}
              {pagination.total} total records (showing {pagination.per_page} per page)
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn-search"
                style={{ padding: "6px 14px" }}
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                className="btn-search"
                style={{ padding: "6px 14px" }}
                disabled={!pagination.has_more || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}